import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ApiService from "services/ApiService";
import ToastUtils from "utils/ToastUtils";
import ValidationService from "services/ValidationService";
import _ from "lodash";
import Input from "components/CustomFields/Input";
import CompaniesSelect from "components/CustomFields/CompaniesSelect";
import moment from "moment";

function InvoicesSubmit() {
  const [vatNumber, setVatNumber] = useState("");
  const [vatNumberError, setVatNumberError] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [companyError, setCompanyError] = useState(false);
  const [series, setSeries] = useState("");
  const [seriesError, setSeriesError] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNumberError, setInvoiceNumberError] = useState(false);
  const [clean, setClean] = useState(0);
  const [cleanError, setCleanError] = useState(false);
  const [vat, setVat] = useState(0);
  const [vatError, setVatError] = useState(false);
  const [gross, setGross] = useState(0);

  const handleCompanyChange = (value) => {
    setCompanyError(false);
    setCompanyId(value);
  };

  const handleVatNumberChange = (value) => {
    setVatNumberError(false);
    if (value.length > 9) return;
    setVatNumber(value);
  };

  const handleSeriesChange = (value) => {
    setSeriesError(false);
    setSeries(value);
  };

  const handleInvoiceNumberChange = (value) => {
    setInvoiceNumberError(false);
    setInvoiceNumber(value);
  };

  const handleCleanChange = (value) => {
    setCleanError(false);
    setClean(value);
    const total = _.round(_.toNumber(vat) + _.toNumber(value), 2);
    setGross(total);
  };

  const handleVatChange = (value) => {
    setVatError(false);
    setVat(value);
    const total = _.round(_.toNumber(clean) + _.toNumber(value), 2);
    setGross(total);
  };

  const handleValidationError = (error) => {
    let pathName = _.get(error, "details[0].path[0]");
    switch (pathName) {
      case "vatNumber":
        setVatNumberError(true);
        return;
      case "series":
        setSeriesError(true);
        return;
      case "aa":
        setInvoiceNumberError(true);
        return;
      case "clean":
        setCleanError(true);
        return;
      case "vatAmount":
        setVatError(true);
        return;
      default:
        ToastUtils.error("Αδυναμία επαλήθευσης στοιχείων!");
    }
  };

  const submitInvoice = async () => {
    const invoice = {
      vatNumber: vatNumber === "" ? null : vatNumber,
      series: series,
      aa: invoiceNumber,
      cleanAmount: clean,
      vatAmount: vat,
      grossAmount: gross,
    };

    if (!companyId) {
      setCompanyError(true);
      return;
    }

    const valErrors = ValidationService.validateUnsignedInvoice(invoice);

    if (valErrors) {
      handleValidationError(valErrors);
      return;
    }

    const finalInvoice = {
      invoice: {
        invoiceHeader: {
          series: series,
          aa: invoiceNumber,
          issueDate: moment().format("YYYY-MM-DD"),
        },
        invoiceSummary: {
          totalNetValue: clean,
          totalVatAmount: vat,
          totalGrossValue: gross,
        },
      },
    };
    const response = await ApiService.postData(
      `/api/v1/invoices/send-invoice-unsigned?companyId=${companyId}`,
      finalInvoice
    );

    if (!response) {
      ToastUtils.error("Αδυναμία καταχώρησης παραστατικού");
    } else {
      ToastUtils.success("Επιτυχής καταχώρηση παραστατικού");
      setClean(0);
      setVat(0);
      setGross(0);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mt={3} mb={4}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={9}>
            <Card sx={{ overflow: "visible" }}>
              <SoftBox p={2} lineHeight={1}>
                <SoftTypography variant="h6" fontWeight="medium">
                  Χειροκίνητη Καταχώρηση Παραστατικού
                </SoftTypography>
                <SoftTypography variant="button" fontWeight="regular" color="text">
                  Καταχωρήστε τα παραστατικά της επιχείρησής σας χειροκίνητα σε περίπτωση αδυναμίας
                  επικοινωνίας με τον πάροχο.
                </SoftTypography>
                <Divider />

                <Grid container spacing={3}>
                  <CompaniesSelect
                    xs={12}
                    md={12}
                    handler={handleCompanyChange}
                    error={companyError}
                  />
                </Grid>
                <Grid container spacing={3}>
                  <Input
                    xs={12}
                    md={12}
                    title="ΑΦΜ Πελάτη"
                    handler={handleVatNumberChange}
                    type="number"
                    value={vatNumber}
                    error={vatNumberError}
                  />
                </Grid>

                <Grid container spacing={3}>
                  <Input
                    xs={12}
                    md={6}
                    title="Σειρά Παραστατικού"
                    handler={handleSeriesChange}
                    initialValue={series}
                    error={seriesError}
                  />
                  <Input
                    xs={12}
                    md={6}
                    title="Αύξων Αριθμός Παραστατικού (Α/Α)"
                    type="number"
                    handler={handleInvoiceNumberChange}
                    initialValue={invoiceNumberError}
                  />
                </Grid>
                <Grid container spacing={3}>
                  <Input
                    xs={12}
                    md={4}
                    title="Καθαρή Αξία"
                    type="number"
                    handler={handleCleanChange}
                    initialValue={clean}
                    error={cleanError}
                  />
                  <Input
                    xs={12}
                    md={4}
                    title="Αξία ΦΠΑ"
                    type="number"
                    handler={handleVatChange}
                    initialValue={vat}
                    error={vatError}
                  />
                  <Input xs={12} md={4} title="Μεικτή Αξία" initialValue={gross} disabled={true} />
                </Grid>

                <SoftBox display="flex" justifyContent="flex-end" mt={3}>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    style={{ width: "100%" }}
                    onClick={submitInvoice}
                  >
                    ΚΑΤΑΧΩΡΗΣΗ
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InvoicesSubmit;
